import {AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  LabAutomationFeature,
  InsightsFeature,
  RegistryFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getStepTypeIconOrDefault} from 'pipelines/StepIconUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {DataFrameCreationMethod, DataFrameCreationMethodOptions} from './DataFrameCreationTypes';

const getStepTypeIcon = (stepType: PipelineStepType): BenchlingIconName =>
  getStepTypeIconOrDefault(stepType, 'table');

export const DATA_FRAME_CREATION_METHOD_OPTIONS: {
  [key in DataFrameCreationMethod]: DataFrameCreationMethodOptions;
} = {
  [DataFrameCreationMethod.CONNECTION_DATA]: {
    icon: getStepTypeIcon(PipelineStepType.CONNECTION_DATA_DATASET),
    initializationViewItem: {type: AnalysisMainViewItemType.CONNECTION_DATA_DATASET_CONFIGURATION},
    text: 'From connection',
    analyticsEventName: 'connection_data',
    isEnabled: () =>
      isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER}) &&
      BenchlingConfig.ENABLE_ANALYSIS_TOOL_CONNECTION_DATASET_CREATION,
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'connectionData'},
  },
  [DataFrameCreationMethod.DATA_CATALOG]: {
    icon: getStepTypeIcon(PipelineStepType.DATA_CATALOG_DATASET),
    initializationViewItem: {type: AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION},
    text: 'From dataset',
    analyticsEventName: 'data_catalog',
    isEnabled: () => BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS,
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'dataCatalog'},
  },
  [DataFrameCreationMethod.INSIGHTS_BLOCK]: {
    icon: getStepTypeIcon(PipelineStepType.INITIALIZE_DATASET),
    initializationViewItem: {type: AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION},
    text: 'From dashboard query',
    analyticsEventName: 'insights_block',
    isEnabled: () => isFeatureEnabled({feature: InsightsFeature.INSIGHTS_DISCOVER}),
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'insightsBlock'},
  },
  [DataFrameCreationMethod.REGISTRY_SCHEMA]: {
    icon: getStepTypeIcon(PipelineStepType.REGISTRY_SCHEMA_DATASET),
    initializationViewItem: {type: AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION},
    // Registry is capitalized because it is a product
    text: 'From Registry schema',
    analyticsEventName: 'registry_schema',
    isEnabled: () => isFeatureEnabled({feature: RegistryFeature.REGISTRY_DISCOVER}),
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'registrySchema'},
  },
  [DataFrameCreationMethod.PLATE_SCHEMA]: {
    icon: getStepTypeIcon(PipelineStepType.PLATE_SCHEMA_DATASET),
    initializationViewItem: {type: AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION},
    text: 'From plate schema',
    analyticsEventName: 'plate_schema',
    isEnabled: () => BenchlingConfig.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS,
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'plateSchema'},
  },
  [DataFrameCreationMethod.UPLOAD_CSV]: {
    icon: getStepTypeIcon(PipelineStepType.UPLOAD_CSV_DATASET),
    initializationViewItem: {type: AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION},
    text: 'From CSV',
    analyticsEventName: 'upload_csv',
    isEnabled: () => true,
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'uploadCSV'},
  },
  [DataFrameCreationMethod.COMBINE_DATA_FRAMES]: {
    icon: getStepTypeIcon(PipelineStepType.JOIN),
    initializationViewItem: {type: AnalysisMainViewItemType.COMBINE_DATA_FRAMES},
    text: 'By combining tables',
    analyticsEventName: 'combine_data_frames',
    isEnabled: () => true,
    canReplace: true,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'combineTables'},
  },
  [DataFrameCreationMethod.LOOK_UP]: {
    icon: getStepTypeIcon(PipelineStepType.LOOK_UP),
    initializationViewItem: {type: AnalysisMainViewItemType.LOOK_UP},
    text: 'From look up',
    analyticsEventName: 'look_up',
    isEnabled: () => true,
    canReplace: false,
    dataTestAttrs: {component: 'DataFrameCreationMethod', element: 'lookUp'},
  },
};

export const ORDERED_DATA_FRAME_CREATION_METHODS: Array<DataFrameCreationMethod> = [
  DataFrameCreationMethod.INSIGHTS_BLOCK,
  DataFrameCreationMethod.REGISTRY_SCHEMA,
  DataFrameCreationMethod.PLATE_SCHEMA,
  DataFrameCreationMethod.DATA_CATALOG,
  DataFrameCreationMethod.CONNECTION_DATA,
  DataFrameCreationMethod.UPLOAD_CSV,
  DataFrameCreationMethod.COMBINE_DATA_FRAMES,
  DataFrameCreationMethod.LOOK_UP,
];

/** @package */
export const DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT = 'No table preview available yet';
